import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, GraduationCap, Radio, X, Users } from 'lucide-react';
import {
  CoachPanel, GuidePanel, ConflictPanel, ConflictCoachPanel, OL,
} from '@/components/OperationListenParts';

const BG = '#141410';
const SURFACE = '#1E1E1A';
const SURFACE2 = '#28281F';
const BORDER = '#2E2E2A';
const CLAY = '#C45A3B';
const CREAM = '#F5F2EB';
const DIM = 'rgba(245,242,235,0.55)';
const MUTED = 'rgba(245,242,235,0.35)';

const API = process.env.REACT_APP_BACKEND_URL;
// Student's Lounge is public — students join by 4-digit code, no login required.
// Backend GET /tc/session/:code has no auth requirement.

const CodeEntry = ({ onJoin, error, onBack }) => {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    if (code.length === 4) onJoin(code);
  };

  return (
    <div data-ol-dark style={{ minHeight: '100vh', background: BG, color: CREAM, padding: 24, paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))' }} data-testid="students-lounge-entry">
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        <button onClick={onBack} data-testid="sl-back"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
            border: 0, color: DIM, cursor: 'pointer', marginBottom: 24, fontSize: 13 }}>
          <ArrowLeft size={16} /> Hub
        </button>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <GraduationCap size={16} color={CLAY} />
          <span style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
            Student&apos;s Lounge
          </span>
        </div>
        <h1 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 40, lineHeight: 1.05, marginBottom: 10 }}>
          Follow along.
        </h1>
        <p style={{ color: DIM, fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
          Enter the 4-digit code from your teacher&apos;s screen. Your phone will mirror the slide they&apos;re on.
          No login needed — anyone with the code can watch.
        </p>

        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: MUTED, marginBottom: 10 }}>
            Session code
          </label>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="0000"
            data-testid="sl-code-input"
            style={{
              width: '100%',
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: '18px 20px',
              color: CREAM,
              fontSize: 32,
              letterSpacing: '0.35em',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              textAlign: 'center',
              outline: 'none',
              marginBottom: 16,
            }}
          />
          {error && (
            <div style={{ color: '#E88066', fontSize: 13, marginBottom: 14 }} data-testid="sl-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={code.length !== 4} data-testid="sl-join-btn"
            style={{
              width: '100%',
              background: code.length === 4 ? CLAY : `${CLAY}44`,
              border: 0,
              borderRadius: 999,
              padding: '14px 20px',
              color: '#fff',
              fontWeight: 600,
              fontSize: 15,
              cursor: code.length === 4 ? 'pointer' : 'not-allowed',
            }}>
            Join session
          </button>
        </form>
      </div>
    </div>
  );
};

const WaitingView = ({ session, onLeave }) => (
  <div data-ol-dark style={{
    minHeight: '100vh', background: BG, color: CREAM,
    display: 'flex', flexDirection: 'column',
  }} data-testid="sl-waiting">
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
      paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
      borderBottom: `1px solid ${BORDER}`, background: SURFACE,
    }}>
      <button onClick={onLeave} data-testid="sl-leave"
        style={{ background: 'transparent', border: 0, color: DIM, cursor: 'pointer', padding: 6 }}>
        <X size={18} />
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600 }}>
          Live session · {session.code}
        </div>
        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
          {session.teacherName}
        </div>
      </div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: CLAY, fontWeight: 600,
      }}>
        <Radio size={11} />
        Standby
      </span>
    </div>

    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px 22px' }}>
      <div style={{ width: '100%', maxWidth: 640, textAlign: 'center' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 10 }}>
          Standing by
        </div>
        <h1 style={{
          fontFamily: '"DM Serif Display", Georgia, serif',
          fontSize: 'clamp(2rem, 6vw, 3.2rem)', lineHeight: 1.1, letterSpacing: '-0.015em',
          marginBottom: 14,
        }}>
          You&apos;re in the room.
        </h1>
        <p style={{ fontSize: 15, color: DIM, lineHeight: 1.6, marginBottom: 26 }}>
          When the teacher launches Operation Listen, the live drill appears here automatically.
          Meanwhile, keep the field guide open for reference.
        </p>
        <a
          href="/modules/training-day-field-guide.html"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sl-field-guide-link"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 20px', borderRadius: 999,
            background: CLAY, color: '#fff', textDecoration: 'none',
            fontSize: 13, fontWeight: 600, letterSpacing: '0.02em',
          }}
        >
          Open Training Day Field Guide ↗
        </a>
      </div>
    </div>
  </div>
);

const EndedView = ({ onBack }) => (
  <div data-ol-dark style={{ minHeight: '100vh', background: BG, color: CREAM, padding: 40, paddingTop: 'calc(40px + env(safe-area-inset-top, 0px))',
    display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="sl-ended">
    <div style={{ maxWidth: 420, textAlign: 'center' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: CLAY, fontWeight: 600, marginBottom: 10 }}>
        Session ended
      </div>
      <h1 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 34, marginBottom: 14 }}>
        Thanks for following along.
      </h1>
      <p style={{ color: DIM, fontSize: 15, lineHeight: 1.6, marginBottom: 22 }}>
        The teacher closed the room. Ask for a new code to rejoin.
      </p>
      <button onClick={onBack} data-testid="sl-ended-back"
        style={{ background: CLAY, border: 0, borderRadius: 999, padding: '12px 22px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
        Back to the Hub
      </button>
    </div>
  </div>
);

// ─── Demo room mirror ─────────────
// When the teacher launches an Operation Listen demo from Teacher's Corner
// during a live session, students in the lounge get a live 4-panel view
// of the drill. Read-only — write props are no-ops so students can't mutate state.
const noop = () => {};

const DemoRoomView = ({ session, onLeave }) => {
  const state = session.demoRoom?.state || {};
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ['Coach', 'Guide', 'Conflict', 'CC'];

  return (
    <div data-ol-dark data-testid="sl-demo-mirror" style={{
      minHeight: '100vh', background: OL.bg, color: OL.text,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))',
        minHeight: 52, borderBottom: `1px solid ${OL.border}`,
        background: OL.surface, position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={onLeave} style={{
          color: OL.textDim, background: 'transparent', border: 0,
          display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
        }} data-testid="sl-demo-leave">
          <ArrowLeft size={16} /> Leave
        </button>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 999,
          background: `${OL.clay}22`, border: `1px solid ${OL.clay}66`,
          color: OL.clay, fontSize: 10, fontWeight: 600,
          letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>
          <Users size={12} /> Watching live · {session.code}
        </span>
      </div>

      {/* Tabs (always shown — students always focus on one role) */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: `1px solid ${OL.border}`,
        background: OL.surface, position: 'sticky', top: 52, zIndex: 9,
      }}>
        {tabs.map((label, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            data-testid={`sl-demo-tab-${i}`}
            style={{
              flex: 1, padding: '10px 6px', background: 'transparent', border: 0,
              borderBottom: `2px solid ${activeTab === i ? OL.clay : 'transparent'}`,
              color: activeTab === i ? OL.cream : OL.textDim,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.14em',
            }}
          >{label}</button>
        ))}
      </div>

      {/* Single-panel view — read-only. All write handlers are no-ops. */}
      <div style={{
        flex: 1, padding: 12, maxWidth: 720, width: '100%',
        margin: '0 auto', pointerEvents: 'none',
      }}>
        {activeTab === 0 && <CoachPanel state={state} write={noop} testPrefix="sl-demo" />}
        {activeTab === 1 && <GuidePanel state={state} write={noop} testPrefix="sl-demo" />}
        {activeTab === 2 && <ConflictPanel state={state} write={noop} testPrefix="sl-demo" />}
        {activeTab === 3 && <ConflictCoachPanel state={state} write={noop} testPrefix="sl-demo" />}
      </div>
    </div>
  );
};

const StudentsLounge = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [ended, setEnded] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const join = useCallback(async (code) => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/tc/session/${code}`);
      setSession(res.data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) setError('No live session with that code. Check with your teacher.');
      else setError('Could not join. Try again.');
    }
  }, []);

  const poll = useCallback(async (code) => {
    try {
      const res = await axios.get(`${API}/api/tc/session/${code}`);
      setSession(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        stopPolling();
        setEnded(true);
        setSession(null);
      }
    }
  }, [stopPolling]);

  useEffect(() => {
    if (session?.code) {
      pollRef.current = setInterval(() => poll(session.code), 1500);
      return () => stopPolling();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.code]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const goBack = () => {
    stopPolling();
    // If the student is logged in, back to their hub; otherwise back to login.
    const token = (() => { try { return localStorage.getItem('gntl_token'); } catch (_) { return null; } })();
    navigate(token ? '/client/hub' : '/login');
  };

  if (ended) return <EndedView onBack={goBack} />;
  if (!session) return <CodeEntry onJoin={join} error={error} onBack={goBack} />;

  // If the teacher has bound a live Operation Listen demo room to this session,
  // switch the student's view to a read-only mirror of the 4-panel drill.
  if (session.demoRoom) {
    return <DemoRoomView session={session} onLeave={goBack} />;
  }

  // Otherwise, wait for the drill and show the training-day field guide link.
  return <WaitingView session={session} onLeave={goBack} />;
};

export default StudentsLounge;
